import React, { useState, useEffect, useCallback, memo } from 'react';
import './BotStatus.css';

const BotCard = memo(({ bot, onRefresh, logsOpen, onToggleLogs }) => {
  const isOnline = bot.status === 'online';
  const timeSincePing = (Date.now() - bot.lastPing) / 1000;

  return (
    <div className={`bot-card ${isOnline ? 'bot-online' : 'bot-stale'}`}>
      <div className="bot-card-header">
        <span className="bot-status-dot" />
        <span className="bot-account-id">{bot.accountId}</span>
        <span className="bot-machine">{bot.machineId}</span>
        <button className="bot-refresh-btn btn btn-sm btn-outline-secondary" onClick={onRefresh}>
          Refresh
        </button>
      </div>
      <div className="bot-card-body">
        <div className="bot-meta-row">
          <span className="bot-label">Status:</span>
          <span className={`bot-value bot-${bot.status}`}>{bot.status}</span>
        </div>
        <div className="bot-meta-row">
          <span className="bot-label">Last ping:</span>
          <span className="bot-value">{timeSincePing < 2 ? 'just now' : `${Math.round(timeSincePing)}s ago`}</span>
        </div>
        <div className="bot-events">
          <span className="bot-label">Active events:</span>
          <div className="bot-event-tags">
            {bot.activeEvents.length === 0
              ? <span className="bot-no-events">none</span>
              : bot.activeEvents.map((ev, i) => <span key={i} className="bot-event-tag">{ev}</span>)
            }
          </div>
        </div>
        {(bot.logs && bot.logs.length > 0) && (
          <div className="bot-logs-section">
            <button className="bot-logs-toggle" onClick={() => onToggleLogs && onToggleLogs(bot.accountId)}>
              {logsOpen ? '▼ Hide logs' : `▶ Show logs (${bot.logs.length})`}
            </button>
            {logsOpen && (
              <pre className="bot-logs">
                {bot.logs.map((line, i) => <div key={i}>{typeof line === 'string' ? line : line.message}</div>)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

function BotStatus() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLogs, setShowLogs] = useState({});

  const toggleLogs = (accountId) => {
    setShowLogs(prev => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const fetchBots = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch('/bots/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      const enriched = data.map(bot => ({
        ...bot,
        lastPing: Date.now() - (bot.status === 'online' ? Math.random() * 10000 : 65000),
      }));
      setBots(enriched);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBots();
    const interval = setInterval(fetchBots, 5000);
    return () => clearInterval(interval);
  }, [fetchBots]);

  return (
    <div className="bot-status-page">
      <h3>Satellite Bot Status</h3>
      <div className="bot-summary">
        <span className="bot-count">
          {bots.filter(b => b.status === 'online').length} online / {bots.length} total
        </span>
      </div>
      {loading && <div className="bot-loading">Loading...</div>}
      {error && <div className="bot-error">Error: {error}</div>}
      {!loading && !error && bots.length === 0 && (
        <div className="bot-empty">
          <p>No active bots configured yet.</p>
        </div>
      )}
      <div className="bot-grid">
        {bots.map((bot, i) => (
          <BotCard key={bot.machineId || i} bot={bot} onRefresh={fetchBots} logsOpen={!!showLogs[bot.accountId]} onToggleLogs={toggleLogs} />
        ))}
      </div>
    </div>
  );
}

export default BotStatus;
